import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AccentCard, Eyebrow } from '../components/design';

/**
 * Phase 26.6 — standalone public Works Cited reference page.
 *
 * Renders the full §13 citations from docs/METHODOLOGY.md in a clean,
 * print-friendly, document-style layout. Linked from the footer and from
 * the /methodology page. The DATA_FRESHNESS table mirrors the freshness
 * status the quarterly data-check agent monitors; AGENT_LAST_RUN is a
 * static string that the agent script can rewrite after each run.
 */

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'var(--tco-bg)',
  fontFamily: 'var(--tco-font-sans)',
  color: 'var(--tco-ink)',
};

const containerStyle: React.CSSProperties = {
  maxWidth: 880,
  margin: '0 auto',
  padding: '40px 32px 80px',
};

const headingStyle: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 600,
  letterSpacing: '-0.015em',
  margin: '8px 0 12px',
  color: 'var(--tco-ink)',
};

const introStyle: React.CSSProperties = {
  fontSize: 15,
  lineHeight: 1.6,
  color: 'var(--tco-ink-2)',
  marginBottom: 32,
  maxWidth: 720,
};

const bodyStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--tco-ink-2)',
};

const categoryTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--tco-ink)',
  margin: '0 0 10px',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
  marginTop: 8,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  background: 'var(--tco-line-soft)',
  borderBottom: '1px solid var(--tco-line)',
  color: 'var(--tco-ink-3)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--tco-line-soft)',
  color: 'var(--tco-ink-2)',
  verticalAlign: 'top',
};

const linkStyle: React.CSSProperties = {
  color: 'var(--tco-accent)',
  textDecoration: 'none',
  borderBottom: '1px solid var(--tco-line)',
  paddingBottom: 1,
  wordBreak: 'break-word',
};

const sectionGap = 20;

/**
 * Last run timestamp of the quarterly data-freshness agent. The agent
 * script (scripts/quarterly_data_check.py) updates this string when it
 * runs so the page advertises freshness honestly. Update via the agent
 * or by hand at each phase that touches data vintages.
 */
const AGENT_LAST_RUN = 'May 30, 2026';

// ── Citation data (mirrors docs/METHODOLOGY.md §13) ─────────────────────────

interface Citation {
  source: React.ReactNode;
  vintage: string;
  description: string;
  url?: string;
  urlLabel?: string;
}

interface CitationCategory {
  heading: string;
  citations: Citation[];
}

const CATEGORIES: CitationCategory[] = [
  {
    heading: 'Vehicle Depreciation',
    citations: [
      {
        source:
          'Argonne National Laboratory — Costs, Effectiveness, and Deployment of Fuel Economy Technologies for Light-Duty Vehicles (ANL/ESD-22/2)',
        vintage: '2022 (Rev. 1, July 2022)',
        description:
          'Residual-value retention curves by powertrain (BEV, HEV, ICEV, PHEV), market segment, and size class. Light-duty curves extracted directly from PDF coefficients; medium-duty (Class 3-7) curves extend the framework as argonne_synthesis.',
        url: 'https://doi.org/10.2172/1891682',
        urlLabel: 'doi.org/10.2172/1891682',
      },
      {
        source: 'iSeeCars — Vehicle Depreciation and Resale Value Analysis',
        vintage: 'Retrieved March 2026',
        description:
          'Cross-reference for light-duty resale and depreciation trends.',
        url: 'https://www.iseecars.com',
        urlLabel: 'iseecars.com',
      },
    ],
  },
  {
    heading: 'Fuel Economy (MPG)',
    citations: [
      {
        source: 'U.S. Environmental Protection Agency — Fuel Economy Guide',
        vintage: 'Live (retrieved at calculation time)',
        description:
          'Test-cycle city / highway / combined MPG and kWh-per-100mi for passenger vehicles and EVs, via the fueleconomy.gov API.',
        url: 'https://fueleconomy.gov',
        urlLabel: 'fueleconomy.gov',
      },
      {
        source: 'Manufacturer specification sheets (commercial vehicles)',
        vintage: 'Per model year',
        description:
          'Published MPG ratings for Class 3-7 commercial vehicles where EPA test data does not apply. Cited per-vehicle in the MSRP table.',
      },
    ],
  },
  {
    heading: 'Fuel Prices',
    citations: [
      {
        source:
          'U.S. Energy Information Administration — Weekly Retail Gasoline and Diesel Prices',
        vintage: 'Live (weekly, via EIA APIv2 PET series)',
        description:
          'Live retail gasoline and diesel prices, plus EIA Electric Power Monthly state averages for electricity. Used as the fallback tier in the per-vehicle → scenario → EIA fuel-price resolution chain.',
        url: 'https://www.eia.gov/petroleum/gasprices/',
        urlLabel: 'eia.gov/petroleum/gasprices',
      },
    ],
  },
  {
    heading: 'Fleet Vocation Fuel Derate',
    citations: [
      {
        source:
          'National Renewable Energy Laboratory — Fleet DNA: Commercial Fleet Vehicle Operating Data',
        vintage: '2014 (data collection) — known stale, flagged',
        description:
          'Composite dataset of 4,705 vehicle-days. Kinematic profiles by vocation and vehicle class drive the real-world MPG derate applied to manufacturer-rated commercial fuel economy. 2014 vintage; recalibration recommended when newer commercial telematics data is available.',
        url: 'https://www.nrel.gov/transportation/fleetdna.html',
        urlLabel: 'nrel.gov/transportation/fleetdna.html',
      },
    ],
  },
  {
    heading: 'Vehicle Maintenance Cost Model',
    citations: [
      {
        source: 'National Private Truck Council — NPTC Fleet Operating Cost Survey',
        vintage: '2022',
        description:
          'Annual fleet maintenance benchmarks by vehicle class. Anchors the base annual maintenance cost for light commercial, medium duty, and heavy duty cohorts.',
      },
      {
        source: 'ARI Fleet / Holman Enterprises — Fleet Cost Benchmarking Report',
        vintage: 'Annual',
        description:
          'Light-commercial fleet maintenance benchmarking cross-reference.',
      },
      {
        source: 'AAA — Your Driving Costs',
        vintage: '2023',
        description:
          'Passenger-vehicle maintenance baseline ($800/yr at the 15k-mile reference).',
      },
      {
        source: 'Fitch, J.W. — Motor Fleet Management (4th ed.)',
        vintage: '1994',
        description:
          'Power-law cost-elasticity model form underlying the mileage-scaled maintenance curve.',
      },
      {
        source: 'Vincentric LLC — Fleet Cost Analysis Methodology',
        vintage: 'Annual',
        description: 'Medium-duty power-law exponent reference.',
      },
      {
        source: 'American Trucking Associations — Cost Data',
        vintage: 'Annual',
        description: 'Heavy-duty maintenance exponent reference.',
      },
    ],
  },
  {
    heading: 'Vocation Intensity (KI Modifiers)',
    citations: [
      {
        source:
          'National Renewable Energy Laboratory — Fleet DNA Kinematic Summary',
        vintage: '2014 (data collection) — known stale, flagged',
        description:
          'Median Kinematic Intensity (KI) per vocation, square-root compressed to defensible maintenance-cost ratios and normalized to long_haul = 1.00. local_delivery is capped at the refuse modifier (1.85) pending newer data.',
        url: 'https://www.nrel.gov/transportation/fleetdna.html',
        urlLabel: 'nrel.gov/transportation/fleetdna.html',
      },
    ],
  },
  {
    heading: 'Federal Tax Benefit',
    citations: [
      {
        source: 'Internal Revenue Code §179 (26 U.S.C. § 179)',
        vintage: 'Current',
        description:
          'First-year expensing of qualifying business property up to the annual limit.',
      },
      {
        source: 'Internal Revenue Code §168(k) (26 U.S.C. § 168(k))',
        vintage: 'Current (TCJA phase-out schedule)',
        description:
          'Bonus depreciation on the basis remaining after §179. Phasing out: 60% (2024), 40% (2025), 20% (2026), 0% (2027+).',
      },
      {
        source: 'IRS Revenue Procedure 2024-21',
        vintage: '2024',
        description:
          'Luxury automobile depreciation caps for Class 1-2 passenger vehicles ($12,400 first-year, 2024 model year).',
      },
      {
        source: 'Tax Cuts and Jobs Act of 2017',
        vintage: '2017',
        description: 'Bonus depreciation phase-out schedule.',
      },
    ],
  },
  {
    heading: 'Vehicle MSRP',
    citations: [
      {
        source:
          'Kelley Blue Book, Edmunds, and manufacturer suggested retail price lists',
        vintage: 'March 2026',
        description:
          'Two-source verification rule: every database MSRP confirmed by at least two independent sources. Commercial-vehicle MSRPs come from dealer quotes where manufacturer pricing is not publicly listed.',
      },
    ],
  },
  {
    heading: 'Fuel Inflation and Insurance Step-Up Defaults',
    citations: [
      {
        source: 'U.S. Energy Information Administration — Annual Energy Outlook 2026',
        vintage: '2026',
        description:
          'Reference-case fuel-price escalation projections. Defaults: diesel 2.5%/yr, gasoline 2.3%/yr, electricity 2.0%/yr.',
        url: 'https://www.eia.gov/aeo',
        urlLabel: 'eia.gov/aeo',
      },
      {
        source:
          'Council of Insurance Agents & Brokers — Commercial Lines Insurance Pricing Survey (CLIPS)',
        vintage: '2024 (annual)',
        description:
          'Commercial auto premium step-up benchmark. Defaults: commercial fleet 6.0%/yr, personal vehicle 5.0%/yr (BLS CPI auto-insurance sub-index).',
      },
    ],
  },
  {
    heading: 'Opportunity Cost',
    citations: [
      {
        source: 'Vanguard and Fidelity long-term index fund return data',
        vintage: 'Long-run averages',
        description:
          'Opportunity cost computed as the return on capital tied up in the vehicle (average committed capital × rate × years). Default investment return rate 7.0% (5-7% range), user-adjustable.',
      },
    ],
  },
  {
    heading: 'Warranty Duration',
    citations: [
      {
        source:
          'Manufacturer warranty documentation — Isuzu, Hino, Ford, Mercedes-Benz, Ram, GM, Freightliner',
        vintage: 'Verified May 2026',
        description:
          'Bumper-to-bumper and powertrain warranty terms (years and miles) keyed by make / fuel type / GVWR class. Ram fleet exclusion from the 10-year retail warranty confirmed via Stellantis press release and Mopar warranty documentation — fleet scenarios use the 5-year terms.',
      },
    ],
  },
  {
    heading: 'Unscheduled Repair Costs',
    citations: [
      {
        source:
          'NPTC Fleet Operating Cost Survey, ATA commercial vehicle cost data, ARI/Holman fleet benchmarks',
        vintage: '2022',
        description:
          'Annual repair-cost defaults by class with power-law mileage scaling. The 0.35 partial-coverage factor (Zone B) derives from NPTC repair cost distribution data showing powertrain failures represent ~65% of commercial fleet repair spend.',
      },
    ],
  },
];

// ── Data freshness table ────────────────────────────────────────────────────

interface FreshnessRow {
  source: string;
  vintage: string;
  cadence: string;
  status: string;
  tone: 'live' | 'current' | 'stale';
}

const FRESHNESS_ROWS: FreshnessRow[] = [
  { source: 'EIA Fuel Prices', vintage: 'Live', cadence: 'Weekly (API)', status: 'Live', tone: 'live' },
  { source: 'EPA MPG', vintage: 'Live', cadence: 'Annual model year', status: 'Live', tone: 'live' },
  { source: 'Argonne ANL depreciation', vintage: '2022', cadence: 'Publication-based', status: 'Current', tone: 'current' },
  { source: 'NREL Fleet DNA KI', vintage: '2014', cadence: 'N/A — known stale', status: 'Stale (flagged)', tone: 'stale' },
  { source: 'MSRP table', vintage: 'March 2026', cadence: 'Semi-annual', status: 'Current', tone: 'current' },
  { source: 'Warranty data', vintage: 'May 2026', cadence: 'Annual', status: 'Current', tone: 'current' },
  { source: 'Maintenance benchmarks', vintage: '2022', cadence: 'Annual (NPTC)', status: 'Current', tone: 'current' },
  { source: 'Insurance step-up', vintage: '2024', cadence: 'Annual (CIAB)', status: 'Current', tone: 'current' },
  { source: 'Fuel inflation', vintage: '2026', cadence: 'Annual (EIA AEO)', status: 'Current', tone: 'current' },
];

function statusBadge(tone: FreshnessRow['tone']): React.CSSProperties {
  const palette: Record<FreshnessRow['tone'], { bg: string; fg: string }> = {
    live: { bg: 'var(--tco-accent-soft)', fg: 'var(--tco-accent)' },
    current: { bg: 'var(--tco-line-soft)', fg: 'var(--tco-ink-2)' },
    stale: { bg: 'var(--tco-neg-soft)', fg: 'var(--tco-neg)' },
  };
  const p = palette[tone];
  return {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '2px 8px',
    borderRadius: 4,
    background: p.bg,
    color: p.fg,
    whiteSpace: 'nowrap',
  };
}

export default function WorksCitedPage() {
  useEffect(() => {
    document.body.classList.add('tco-ledger');
    return () => {
      document.body.classList.remove('tco-ledger');
    };
  }, []);

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <Eyebrow>Reference</Eyebrow>
        <h1 style={headingStyle}>Data Sources &amp; References</h1>
        <p style={introStyle}>
          VehicleTCO calculations rely on the following authoritative sources.
          See our{' '}
          <Link to="/methodology" style={linkStyle}>
            Methodology
          </Link>{' '}
          for how each is applied.
        </p>

        {CATEGORIES.map((category) => (
          <AccentCard key={category.heading} stripe="ink" style={{ marginBottom: sectionGap }}>
            <h2 style={categoryTitleStyle}>{category.heading}</h2>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {category.citations.map((c, idx) => (
                <li
                  key={idx}
                  style={{
                    paddingTop: idx === 0 ? 0 : 12,
                    marginTop: idx === 0 ? 0 : 12,
                    borderTop: idx === 0 ? 'none' : '1px solid var(--tco-line-soft)',
                  }}
                >
                  <div style={{ ...bodyStyle, color: 'var(--tco-ink)', fontWeight: 600 }}>
                    {c.source}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--tco-ink-3)',
                      letterSpacing: '0.02em',
                      margin: '2px 0 4px',
                    }}
                  >
                    {c.vintage}
                  </div>
                  <div style={bodyStyle}>{c.description}</div>
                  {c.url && (
                    <div style={{ marginTop: 4 }}>
                      <a href={c.url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                        {c.urlLabel || c.url}
                      </a>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </AccentCard>
        ))}

        {/* Data freshness ─────────────────────────────────────────────── */}
        <AccentCard stripe={null} style={{ marginTop: 24, marginBottom: sectionGap }}>
          <Eyebrow>Data freshness</Eyebrow>
          <h2 style={{ ...categoryTitleStyle, fontSize: 18, textTransform: 'none', letterSpacing: '-0.005em' }}>
            Source vintage and update cadence
          </h2>
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Vintage</th>
                  <th style={thStyle}>Update cadence</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {FRESHNESS_ROWS.map((row) => (
                  <tr key={row.source}>
                    <td style={{ ...tdStyle, color: 'var(--tco-ink)' }}>{row.source}</td>
                    <td style={tdStyle}>{row.vintage}</td>
                    <td style={tdStyle}>{row.cadence}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(row.tone)}>{row.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ ...bodyStyle, marginTop: 16, fontSize: 13 }}>
            A quarterly automated agent checks primary sources and flags changes.
            Last agent run: <strong style={{ color: 'var(--tco-ink)' }}>{AGENT_LAST_RUN}</strong>.
          </p>
        </AccentCard>

        <div
          style={{
            marginTop: 40,
            paddingTop: 16,
            borderTop: '1px solid var(--tco-line)',
            fontSize: 11,
            color: 'var(--tco-ink-3)',
            letterSpacing: '0.04em',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <Link to="/methodology" style={{ ...linkStyle, color: 'var(--tco-ink-2)', borderBottomColor: 'var(--tco-line)' }}>
            ← Back to methodology
          </Link>
          <Link to="/" style={{ ...linkStyle, color: 'var(--tco-ink-2)', borderBottomColor: 'var(--tco-line)' }}>
            Back to scenarios →
          </Link>
        </div>
      </div>
    </div>
  );
}
